import { ChangeEvent, useRef } from 'react';
import { keepAuthorizationIfUnchanged, useAuthSessionMutation } from '../../lib/authSessionGuard';
import { Col, Row, setItemWithExpiration } from '../../lib/utils';
import { Body2Regular, Body3Regular, CaptionLight } from '../../lib/font';
import { colors } from '../../lib/colors';
import { I_EMPTY_PROFILE } from '../../types/images';
import styled from 'styled-components';
import { modifyProfileImageAPI } from '../../api/mypage';
import UsableState from '../UsableState';
import WhiteSquareButton from '../Button/WhiteSquareButton';
import ColorSquareButton from '../Button/ColorSquareButton';
import TextChangeButton from '../TextButton/TextChangeButton';
import { Camera } from 'lucide-react';

const MAX_BASE64_LENGTH = 4 * 1024 * 1024; // 서버 제한 (~4,194,304자)
const MAX_IMAGE_DIMENSION = 800; // 프로필용 적정 해상도 (가로/세로 최대 px)
const DEFAULT_JPEG_QUALITY = 0.75; // 프로필용 적정 품질

/** 프로필용으로 이미지를 리사이즈·압축 후 Base64로 반환 */
const compressImageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            const scale = Math.min(
                MAX_IMAGE_DIMENSION / img.width,
                MAX_IMAGE_DIMENSION / img.height,
                1
            );
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas를 사용할 수 없습니다.'));
                return;
            }
            ctx.drawImage(img, 0, 0, w, h);

            const tryQuality = (quality: number): void => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('이미지 압축에 실패했습니다.'));
                            return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => {
                            const base64 = reader.result as string;
                            if (base64.length > MAX_BASE64_LENGTH && quality > 0.2) {
                                tryQuality(Math.max(0.2, quality - 0.15));
                            } else {
                                resolve(base64);
                            }
                        };
                        reader.onerror = () => reject(new Error('파일을 읽지 못했습니다.'));
                        reader.readAsDataURL(blob);
                    },
                    'image/jpeg',
                    quality
                );
            };

            tryQuality(DEFAULT_JPEG_QUALITY);
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('이미지를 불러올 수 없습니다.'));
        };
        img.src = url;
    });
};

const ProfileImageCircle = styled.img`
    width: 120px;
    height: 120px;
    border-radius: 60px;
    object-fit: cover;
    display: block;
`;

type Props = {
    authorization: any;
    authObject: any;
    numberTrainers: number;
    numberStudents: number;
    numberActive: number;
    onClickPasswordChange: () => void;
    onClickWithDrawal: () => void;
    onClickConfirmUsingPlan: () => void;
    setAuthObject: any;
    createAlert?: (title: string, content: string, isError?: boolean, buttonText?: string, callback?: () => void) => void;
};

/**
 * 프로필(사진 등) 수정 뒤 저장할 authObject. **기존 authObject 를 펼친 뒤** 바뀐 사용자 필드만 덮는다 —
 * 예전엔 필드 목록을 다시 만들어 정산 방식(settlementType)·부가세(vatPolicy·vatRate) 등이 빠졌고,
 * 그러면 정산 기준 줄·지난 달 급여 안내가 꺼졌다(웹 검증 2026-11-02).
 */
export const buildAuthObjectAfterProfileUpdate = (authObject: any, user: any) => ({
    ...authObject,
    adminId: user?.adminId,
    id: user?.id,
    name: user?.name,
    email: user?.email,
    gender: user?.gender,
    phoneNumber: user?.phoneNumber,
    profileImageUrl: user?.profileImageUrl,
    centerAddress: authObject?.centerAddress,
    centerCategory: authObject?.centerCategory,
    centerCode: authObject?.centerCode,
    centerName: authObject?.centerName,
    centerId: authObject?.centerId,
    isNewCommunicationBox: authObject?.isNewCommunicationBox,
    coachCount: authObject?.coachCount,
    memberCount: authObject?.memberCount,
    activationDate: authObject?.activationDate,
    expirationDate: authObject?.expirationDate,
    expirationDelayDate: authObject?.expirationDelayDate,
    finalPaymentDate: authObject?.finalPaymentDate,
    finalPaymentTryDate: authObject?.finalPaymentTryDate,
    cardIssuingBank: authObject?.cardIssuingBank,
    cardNumber: authObject?.cardNumber,
    planAmount: authObject?.planAmount,
    usableStatus: authObject?.usableStatus,
    paymentDueDate: authObject?.paymentDueDate,
    planLimitCount: authObject?.planLimitCount,
    prevPlanLimitCount: authObject?.prevPlanLimitCount,
    changedPlanLimitCount: authObject?.changedPlanLimitCount,
    confirmUsableStatus: authObject?.confirmUsableStatus,
    testAccount: authObject?.testAccount,
});

const MyPageLeft = ({
    authorization,
    authObject,
    numberTrainers,
    numberStudents,
    numberActive,
    onClickPasswordChange,
    onClickWithDrawal,
    onClickConfirmUsingPlan,
    setAuthObject,
    createAlert,
}: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const applyAuthUpdate = (user: any) => {
        const authObjectData = buildAuthObjectAfterProfileUpdate(authObject, user);
        // 요청 때 토큰을 다시 쓰던 자리 — 지금 저장소 토큰이 같을 때만(갱신된 새 토큰을 되돌리지 않게).
        keepAuthorizationIfUnchanged(authorization);
        setItemWithExpiration('authObject', authObjectData, 12);
        setAuthObject(authObjectData);
    };

    const modifyProfileImage = useAuthSessionMutation({
        mutationFn: async (base64String: string) =>
            await modifyProfileImageAPI(authObject?.adminId ?? '', base64String, authorization ?? ''),
        onSuccess: (res) => {
            const user = res?.data?.user ?? res?.data;
            if (res?.status === 200 && user && (user.profileImageUrl !== undefined || user.adminId)) {
                // 캐시 무효화용 쿼리 추가해 변경된 사진이 바로 반영되도록 함
                const profileImageUrl = user.profileImageUrl
                    ? `${user.profileImageUrl}${user.profileImageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`
                    : user.profileImageUrl;
                applyAuthUpdate({ ...user, profileImageUrl });
                createAlert?.('변경 완료', '프로필 사진이 변경되었습니다.', false);
            } else if (res?.status !== 200) {
                const status = res?.status;
                if (status === 400) {
                    createAlert?.('요청 오류', res?.data?.message ?? '이미지 형식 또는 용량을 확인해 주세요.', true);
                } else if (status === 401 || status === 403) {
                    createAlert?.('로그인 필요', '다시 로그인해 주세요.', true);
                } else {
                    createAlert?.('오류', '프로필 사진 변경에 실패했습니다.', true);
                }
            } else {
                createAlert?.('오류', '프로필 사진 변경에 실패했습니다.', true);
            }
        },
        onError: () => {
            createAlert?.('오류', '프로필 사진 변경에 실패했습니다. 네트워크를 확인해 주세요.', true);
        },
    }, {
        owner: () => ({ authObject, authorization }),
    });

    const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (authObject?.testAccount) {
            createAlert?.('제한', '테스트 계정은 프로필 사진을 변경할 수 없습니다.', true);
            event.target.value = '';
            return;
        }
        if (!authObject?.adminId || !authorization) {
            createAlert?.('오류', '잘못된 요청입니다. 다시 로그인해 주세요.', true);
            event.target.value = '';
            return;
        }
        try {
            const base64String = await compressImageToBase64(file);
            modifyProfileImage.mutate(base64String);
        } catch {
            createAlert?.('오류', '이미지 압축에 실패했습니다. 다른 이미지를 선택해 주세요.', true);
        }
        event.target.value = '';
    };

    return (
        <Col
            style={{
                width: 280,
                height: 828,
                backgroundColor: colors.WHITE_50,
                borderRadius: 16,
                border: '1px solid #C3D4F4',
                alignItems: 'center',
            }}
        >
            <Row style={{ width: 216, marginTop: 24 }}>{authObject && <UsableState authObject={authObject} />}</Row>

            <div style={{ position: 'relative', display: 'inline-block', marginTop: 32 }}>
                <ProfileImageCircle
                    src={authObject?.profileImageUrl || I_EMPTY_PROFILE}
                    alt="프로필"
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                />
                {!authObject?.testAccount && (
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute right-0 bottom-0 flex items-center gap-[6px] px-[10px] py-[6px] rounded-full bg-white border border-GREY_50 shadow-md text-LAVEL_4 hover:bg-GREY_50 hover:border-GREY_200 transition-colors duration-200"
                    >
                        <Camera size={14} strokeWidth={1.8} className="shrink-0" />
                        <span className="text-[1.2rem] font-normal leading-none">사진 수정</span>
                    </button>
                )}
            </div>

            <Col style={{ width: 216, height: 70, marginTop: 32, justifyContent: 'space-between' }}>
                <Body2Regular style={{ color: colors.LAVEL_3 }}>아이디</Body2Regular>
                <Row
                    style={{
                        width: 216,
                        height: 36,
                        borderRadius: 36,
                        backgroundColor: colors.WHITE_500,

                        alignItems: 'center',
                        paddingLeft: 16,
                    }}
                >
                    <Body3Regular style={{ color: colors.LAVEL_1 }}>{authObject?.adminId}</Body3Regular>
                </Row>
            </Col>

            <Col style={{ width: 216, height: 90, marginTop: 32, justifyContent: 'space-between' }}>
                <Body2Regular style={{ color: colors.LAVEL_3 }}>센터코드</Body2Regular>

                <Row
                    style={{
                        width: 216,
                        height: 36,
                        borderRadius: 36,
                        backgroundColor: colors.WHITE_500,
                        alignItems: 'center',
                        paddingLeft: 16,
                    }}
                >
                    <Body3Regular style={{ color: colors.LAVEL_1 }}>{authObject?.centerCode}</Body3Regular>
                </Row>
                <CaptionLight style={{ color: colors.LAVEL_1 }}>{'*변경 불가능한 정보입니다'}</CaptionLight>
            </Col>

            <Col style={{ width: 216, height: 88, justifyContent: 'space-between', marginTop: 32 }}>
                <Body3Regular style={{ color: colors.LAVEL_3 }}>소속 강사 : {numberTrainers}명</Body3Regular>
                <Body3Regular style={{ color: colors.LAVEL_3 }}>전체 회원 : {numberStudents}명</Body3Regular>
                <Body3Regular style={{ color: colors.LAVEL_3 }}>활성 회원 : {numberActive}명</Body3Regular>
            </Col>

            <Col style={{ width: 216, marginTop: 32 }}>
                <WhiteSquareButton
                    customStyle={{ width: 139, height: 36 }}
                    size="small"
                    text={'비밀번호 바꾸기'}
                    callBack={onClickPasswordChange}
                />

                <ColorSquareButton
                    customStyle={{ width: 139, height: 36, marginTop: 12, marginBottom: 98 }}
                    size="small"
                    callBack={onClickConfirmUsingPlan}
                    text={'구독 플랜'}
                    isGradient={true}
                />

                <TextChangeButton
                    customStyle={{ width: 59, height: 24 }}
                    text={'탈퇴하기'}
                    size="middle"
                    callBack={onClickWithDrawal}
                />
            </Col>
        </Col>
    );
};

export default MyPageLeft;
